// Parity P1 wave 1: one task component for three hosts (hybrid design D1, §3.1–3.2): the
// drawer over a Work page, the full page (`/c/$slug/work/t/$id`) and the Inbox preview. The host
// gives the space; inside it two panes (`SplitRail`): the work, and the rail with Conversation,
// Details (the properties) and Activity. TASK-DRAWER-CHAT: no properties column; Status and
// Assignee are chips under the title, the conversation takes the freed width, its messages scroll
// and its composer stays at the bottom. Below 640 px of host the rail stacks under the work.
//
// The work pane is the board's task layout (its own, its space's default, or the built-in one),
// drawn from the block registry; each person folds sections and the rail (`me/prefs`). The
// header carries the breadcrumb, star, Follow bell with its watchers, copy link, full page and
// ⋯; the core row Complete / Reopen. A completion the server refuses over empty required
// fields highlights them (`required_missing`); an open hard dependency says what it waits on.
import { ApiError } from "@companyos/sdk";
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerTitle,
  Notice,
  Skeleton,
  type SplitMode,
  SplitRail,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  CheckIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  RotateCcwIcon,
} from "lucide-react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { doneStatus, isDone, openStatus, taskRef, useSeatPeople } from "../model";
import { PostResultsSection } from "../post-results";
import { useRecordRecent } from "../recents";
import { ResearchSignalBlock } from "../research-signal";
import { TaskTagsSection } from "../tags";
import { TaskActivity, type TaskRailTab } from "../task-activity";
import "../task-layout/blocks";
import { TaskHostContext, type TaskHostContextValue } from "../task-layout/host-context";
import { DEFAULT_LAYOUT, itemBlocks, restFields } from "../task-layout/layout";
import type { TaskBlockProps, TaskCan, TaskHost } from "../task-layout/registry";
import { bodyFieldIds, LayoutBody } from "../task-layout/render";
import { useTaskWrites } from "../task-writes";
import type { TaskDetail } from "../view-engine";
import { boardQuery, boardsQuery, fieldsQuery, spacesQuery, taskQuery } from "../work-queries";
import { AttentionStrip } from "./attention";
import { FieldsSection } from "./fields";
import { TaskHeaderActions, TaskLocation } from "./header";
import { HoldStrip } from "./hold-strip";
import { useTaskKeys } from "./keyboard";
import { TaskLayoutEditor } from "./layout-editor";
import { PropertiesPanel, TaskChips, TitleField } from "./properties";
import { boardLayoutQuery, usePref } from "./queries";
import { ReviewStrip } from "./review-strip";

export type TaskViewProps = {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  host: TaskHost;
  onClose: () => void;
  onOpenTask: (id: string) => void;
  /** The drawer and the Inbox preview: opens the full page (⇧F). */
  onFullPage?: () => void;
  /** A first guess of the host's width (the drawer knows it). */
  initialWidth?: number;
  /** Forces the pane arrangement (the full page keeps its rail). */
  mode?: SplitMode;
};

export function TaskView(props: TaskViewProps) {
  const { api, company, taskId, host } = props;
  const task = useQuery(taskQuery(api, company.id, taskId));
  useRecordRecent(api, company.id, "task", task.data ? taskId : null);
  if (task.isPending)
    return (
      <Chrome {...props} location="Task" actions={null}>
        <Title host={host} sr>
          Loading the task
        </Title>
        <div role="status" aria-label="Loading the task" className="flex flex-col gap-3 p-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Chrome>
    );
  if (task.isError || !task.data)
    return (
      <Chrome {...props} location="Task" actions={null}>
        <div className="p-6">
          <Title host={host}>This task is not available</Title>
          <p className="mt-2 text-sm text-muted-foreground">
            It was deleted, or you cannot see it. Nothing else changed.
          </p>
        </div>
      </Chrome>
    );
  return <Loaded {...props} task={task.data} />;
}

function Title({ host, sr, children }: { host: TaskHost; sr?: boolean; children: ReactNode }) {
  const cls = sr ? "sr-only" : "font-heading text-[20px] font-semibold";
  return host === "drawer" ? (
    <DrawerTitle className={sr ? "sr-only" : undefined}>{children}</DrawerTitle>
  ) : (
    <h1 className={cls}>{children}</h1>
  );
}

/** The host's frame: the drawer's header and scrolling body, or the page's. */
function Chrome({
  host,
  onClose,
  location,
  actions,
  children,
  hostRef,
}: TaskViewProps & {
  location: ReactNode;
  actions: ReactNode;
  children: ReactNode;
  hostRef?: React.RefObject<HTMLDivElement | null>;
}) {
  if (host === "drawer")
    return (
      <>
        <DrawerHeader actions={actions}>{location}</DrawerHeader>
        <DrawerBody
          ref={hostRef}
          // The panes scroll on their own (`SplitRail`), so the body itself does not.
          className="flex flex-col overflow-hidden p-0 [scrollbar-gutter:auto]"
          data-task-host="drawer"
          tabIndex={-1}
        >
          {children}
        </DrawerBody>
      </>
    );
  return (
    <div
      ref={hostRef}
      data-task-host={host}
      tabIndex={-1}
      className="flex min-h-0 flex-1 flex-col outline-none"
    >
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-subtle px-6 max-md:px-4">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={host === "page" ? "Back" : "Close the preview"}
          onClick={onClose}
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <div className="min-w-0 truncate text-xs text-muted-foreground">{location}</div>
        {actions ? <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

function Loaded(props: TaskViewProps & { task: TaskDetail }) {
  const { api, company, task: t, host, onOpenTask, onFullPage } = props;
  const co = company.id;
  const board = useQuery({ ...boardQuery(api, co, t.board_id), enabled: Boolean(t.board_id) });
  const fields = useQuery({ ...fieldsQuery(api, co, t.board_id), enabled: Boolean(t.board_id) });
  const layout = useQuery({
    ...boardLayoutQuery(api, co, t.board_id),
    enabled: Boolean(t.board_id),
  });
  const spaces = useQuery(spacesQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  const people = useSeatPeople(api, co);
  const deps = useQueries({
    queries: t.dependencies.map((d) => taskQuery(api, co, d.depends_on_task_id)),
  });
  const writes = useTaskWrites(api, company);
  const [drawerPref, setDrawerPref] = usePref(api, co, "task_drawer", {});
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // Required fields the refusal counted but this caller cannot see (`hidden`).
  const [hiddenMissing, setHiddenMissing] = useState(0);
  const [customizing, setCustomizing] = useState(false);
  const [railTab, setRailTab] = useState<TaskRailTab>("conversation");
  const hostRef = useRef<HTMLDivElement>(null);

  const b = board.data;
  const allBoards = boards.data?.data ?? [];
  const space = spaces.data?.data.find((s) => s.id === b?.space_id);
  const effective = layout.data?.effective ?? DEFAULT_LAYOUT;
  const fieldList = fields.data?.data ?? [];
  const railOpen = drawerPref.rail !== "closed";
  const collapsed = useMemo(() => new Set(drawerPref.collapsed ?? []), [drawerPref.collapsed]);
  const done = isDone(t, b);
  const can: TaskCan = { edit: true, manage: layout.data?.can_edit ?? false };

  const titleOf = useCallback(
    (id: string) => {
      const hit = deps.find((d) => d.data?.id === id)?.data;
      return hit ? `${taskRef(hit)} ${hit.title}` : null;
    },
    [deps],
  );
  const hostValue: TaskHostContextValue = useMemo(
    () => ({
      people,
      fields: fieldList,
      rules: new Map((layout.data?.fields ?? []).map((r) => [r.field_def_id, r])),
      titleOf,
      missing,
    }),
    [people, fieldList, layout.data?.fields, titleOf, missing],
  );

  const setStatus = (status: string) =>
    writes.setStatus(t.id, status, {
      onError: (e) => {
        if (e instanceof ApiError && e.code === "required_missing") {
          const pb = e.problem as { missing?: string[]; hidden?: number } | null;
          const ids = pb?.missing ?? [];
          setMissing(new Set(ids));
          // A missing field outside the body is in the Details tab: show it (the rail opens in
          // the same preference write as the unfold below).
          const inDetails = ids.some((id) => !placed.has(id));
          if (inDetails) setRailTab("details");
          setHiddenMissing(pb?.hidden ?? 0);
          // Unfold the sections that hold them, so the highlight is on screen.
          const restIds = new Set(
            restFields(
              effective,
              fieldList.map((f) => f.id),
            ),
          );
          const holds = (blocks: string[]) =>
            blocks.some(
              (b) =>
                ids.some((id) => b === `field:${id}`) ||
                (b === "fields:rest" && ids.some((id) => restIds.has(id))),
            );
          const unfold = effective.sections
            .filter((s) => holds(s.columns.flatMap((c) => c.items.flatMap(itemBlocks))))
            .map((s) => s.id)
            .filter((id) => collapsed.has(id));
          const openRail = inDetails && !railOpen;
          if (unfold.length || openRail)
            setDrawerPref({
              ...drawerPref,
              ...(openRail ? { rail: "open" as const } : {}),
              collapsed: [...collapsed].filter((id) => !unfold.includes(id)),
            });
          requestAnimationFrame(() =>
            hostRef.current
              ?.querySelector<HTMLElement>(
                "[data-missing] input, [data-missing] select, [data-missing] button",
              )
              ?.focus(),
          );
        }
      },
    });
  const complete = () => {
    const to = done ? openStatus(b) : doneStatus(b);
    if (to) setStatus(to);
  };
  const showDetails = () => {
    setRailTab("details");
    if (!railOpen) setDrawerPref({ ...drawerPref, rail: "open" });
  };
  const toggleRail = () => setDrawerPref({ ...drawerPref, rail: railOpen ? "closed" : "open" });
  const toggleSection = (id: string) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDrawerPref({ ...drawerPref, collapsed: [...next] });
  };
  useTaskKeys(hostRef, {
    rail: toggleRail,
    complete,
    // D, P, M open menus in the Details tab: show it first.
    details: showDetails,
    ...(onFullPage ? { fullPage: onFullPage } : {}),
  });

  const waiting = t.dependencies
    .filter((d) => d.kind === "hard")
    .map((d) => deps.find((q) => q.data?.id === d.depends_on_task_id)?.data)
    .filter(
      (x): x is TaskDetail =>
        Boolean(x) &&
        !isDone(
          x as TaskDetail,
          allBoards.find((bb) => bb.id === (x as TaskDetail).board_id),
        ),
    );

  const blockProps: TaskBlockProps | null = b
    ? { api, company, task: t, board: b, host, can, onOpenTask }
    : null;
  const placed = bodyFieldIds(effective, fieldList);

  const head = (
    <>
      <TitleField api={api} company={company} task={t} inDialog={host === "drawer"} />
      <TaskChips
        task={t}
        board={b}
        people={people}
        setStatus={setStatus}
        onToggleAssignee={(seat, on) => writes.toggleAssignee(t.id, seat, on)}
      />
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Button
          variant={done ? "outline" : "default"}
          size="sm"
          data-slot="task-complete"
          aria-keyshortcuts="C"
          onClick={complete}
        >
          {done ? <RotateCcwIcon aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}
          {done ? "Reopen" : "Complete"}
        </Button>
        <div className="min-w-0 flex-1">
          <TaskTagsSection api={api} company={company} task={t} />
        </div>
      </div>
      {missing.size || hiddenMissing ? (
        <Notice tone="warning" className="mt-3" data-slot="task-required-missing">
          {missing.size
            ? `Fill the highlighted ${missing.size > 1 ? "fields" : "field"} to complete the task.`
            : "This task can't be completed yet."}
          {hiddenMissing
            ? ` ${hiddenMissing} required ${hiddenMissing > 1 ? "fields" : "field"} you can't see ${hiddenMissing > 1 ? "are" : "is"} empty: ask the task's owner to fill ${hiddenMissing > 1 ? "them" : "it"}.`
            : ""}
        </Notice>
      ) : null}
      {t.blocked ? (
        <Notice tone="warning" className="mt-3" data-slot="task-blocked">
          Waiting on{" "}
          {waiting.length
            ? waiting.map((w, i) => (
                <span key={w.id}>
                  {i ? ", " : ""}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => onOpenTask(w.id)}
                  >
                    {taskRef(w)} {w.title}
                  </button>
                </span>
              ))
            : "another task"}
          : it can't start or finish until {waiting.length > 1 ? "those are" : "that is"} done.
        </Notice>
      ) : null}
    </>
  );
  const railToggle = (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={railOpen ? "Fold the conversation" : "Show the conversation"}
            aria-keyshortcuts="]"
            aria-pressed={railOpen}
            data-slot="rail-toggle"
            onClick={toggleRail}
          />
        }
      >
        {railOpen ? (
          <PanelRightCloseIcon aria-hidden="true" />
        ) : (
          <PanelRightOpenIcon aria-hidden="true" />
        )}
      </TooltipTrigger>
      <TooltipContent>
        {railOpen ? "Fold the conversation (])" : "Show the conversation (])"}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <Chrome
      {...props}
      hostRef={hostRef}
      location={<TaskLocation company={company} task={t} board={b} spaceName={space?.name} />}
      actions={
        <TaskHeaderActions
          api={api}
          company={company}
          task={t}
          canManage={can.manage}
          onClose={props.onClose}
          onOpenTask={onOpenTask}
          onCustomize={() => setCustomizing(true)}
          {...(onFullPage ? { onFullPage } : {})}
        />
      }
    >
      <TaskHostContext.Provider value={hostValue}>
        <AttentionStrip task={t} can={can} />
        <HoldStrip api={api} co={co} taskId={t.id} board={b} />
        {/* BOARD-GATES: a person-only column's Approve · Rework · Reject (refetched per status) */}
        <ReviewStrip api={api} co={co} taskId={t.id} status={t.status} />
        <SplitRail
          data-task-drawer={t.id}
          initialWidth={props.initialWidth ?? 0}
          {...(props.mode ? { mode: props.mode } : {})}
          head={head}
          railOpen={railOpen}
          railToggle={railToggle}
          // Half and half by default; the handle between the panes moves it, per person.
          {...(drawerPref.rail_share !== undefined ? { railShare: drawerPref.rail_share } : {})}
          onRailShareChange={(share) => setDrawerPref({ ...drawerPref, rail_share: share })}
          work={
            blockProps ? (
              <LayoutBody
                {...blockProps}
                layout={effective}
                collapsed={collapsed}
                onToggle={toggleSection}
              />
            ) : null
          }
          rail={
            <div className="contents [&>section]:mt-0">
              <TaskActivity
                api={api}
                company={company}
                taskId={t.id}
                tab={railTab}
                onTabChange={setRailTab}
                details={
                  <>
                    {blockProps ? (
                      <PropertiesPanel
                        api={api}
                        company={company}
                        task={t}
                        board={b}
                        boards={allBoards}
                        people={people}
                        setStatus={setStatus}
                        blockProps={blockProps}
                      />
                    ) : (
                      <Skeleton className="h-40 w-full" />
                    )}
                    <FieldsSection
                      api={api}
                      company={company}
                      task={t}
                      fields={fieldList}
                      placed={placed}
                      canEdit={can.edit}
                    />
                    {/* BOARD-RESULTS: a finding's signal; a published card's results */}
                    <ResearchSignalBlock fields={fieldList} values={t.fields} />
                    <PostResultsSection
                      api={api}
                      co={co}
                      taskId={t.id}
                      published={done}
                      canEdit={can.edit}
                    />
                  </>
                }
                ctx={{
                  people,
                  board: b,
                  fields: fieldList,
                  boardName: (id) => allBoards.find((x) => x.id === id)?.name ?? "another board",
                }}
              />
            </div>
          }
        />
        {customizing && b && layout.data ? (
          <TaskLayoutEditor
            api={api}
            company={company}
            board={b}
            spaceName={space?.name ?? "this space"}
            state={layout.data}
            fields={fieldList}
            sample={t}
            people={people}
            onClose={() => setCustomizing(false)}
          />
        ) : null}
      </TaskHostContext.Provider>
    </Chrome>
  );
}
